<template>
  <v-card
    class="elevation-4 check-in-client-card"
    :class="{ 'check-in-client-card--selected': isSelectedClient }"
    hover
    :ripple="!isSelectedClient"
    @click.native="!isSelectedClient && selectClient(client)">
    <v-card-title primary-title>
      <div class="check-in-client-card__headline headline">
        <span v-if="isSelectedClient">Hey</span> <span>{{ client.FirstName }}  {{ fullClient && fullClient.LastName }}</span>
      </div>
    </v-card-title>
    <v-card-text>
      <v-layout row wrap v-if="!isSelectedClient && !isCheckedIn">
        <v-flex xs6 class="check-in-client-card__value">
          <div>{{ client.Email ? client.Email : 'No Email set for client' }}</div>
          <small>Email</small>
        </v-flex>

        <v-flex xs6 class="check-in-client-card__value">
          <div>{{ client.Phone }}</div>
          <small>Phone</small>
        </v-flex>

         <v-flex xs6 class="check-in-client-card__value">
          <div>{{ hasAppointments ? formatDate(nextAppointment.AppointmentDate) : 'No upcoming appointment' }}</div>
          <small>Next Appointment</small>
        </v-flex>

        <v-flex xs6 class="check-in-client-card__value">
          <div>{{ countPreviousCompletedAppointments > 0 ? countPreviousCompletedAppointments : 'No completed appointments' }}</div>
          <small>Total Completed Appointments</small>
        </v-flex>
      </v-layout>

      <v-layout row wrap v-if="isSelectedClient && appointmentStatus && !isCheckedIn">
        <v-flex xs8 offset-xs2 class="check-in-client-card__message">
          <div v-if="appointmentStatus === 'no-appt'">
            <span v-if="isScheduleThirty">
              It doesn't look like we have you scheduled today for an appointment. Would you like to view a list of available appointments?
            </span>
            <span v-else>
              The wait time is currently {{ waitTime }} min &amp; we will notify you via text when you are next in line. Knowing the wait time, would you like to reserve a spot today?
            </span>
            <!-- requires scheduler -->
          </div>
          <div v-else-if="appointmentStatus === 'not-today'">
            We have an appointment scheduled for you on {{ formatDate(nextAppointment.AppointmentDate) }}, would you like to see our soonest available appointments? 
            <!-- requires scheduler -->
          </div>

          <div v-else-if="!isScheduleThirty && this.hasAppointments">
            Great, your spot has been reserved and you will receive a text message when you are next in line!
          </div>
          <div v-else-if="appointmentStatus === 'beyond-check-in'">
            <span v-if="nextAppointment.Status === 20">You are already checked in. Please wait for the next technician to help you out.</span>
            <span v-else-if="nextAppointment.Status === 30">You are supposed to be in a seat. The technician will (hopefully) find you soon</span>
            <span v-else-if="nextAppointment.Status === 40 || nextAppointment.Status === 50">You are done. Have a good day.</span>
          </div>
          <div v-else-if="appointmentStatus === 'too-late'">
            You are too late to check in for your {{ formatTimeOnly(nextAppointment.AppointmentDate) }} appointment. Please speak to the floor manager located in the 1st chair for accommodations.
          </div>
          <div v-else-if="appointmentStatus === 'too-early'">
            You are too early to check in for your {{ formatTimeOnly(nextAppointment.AppointmentDate) }} appointment. You cannot check in sooner than {{ formatTimeEarly(nextAppointment.AppointmentDate) }}. Enjoy a drink, hangout and check back in soon!
          </div>
          
          <div v-else-if="appointmentStatus === 'is-new-client'">
            Welcome to your {{ formatTimeOnly(nextAppointment.AppointmentDate) }} appointment, {{ fullClient && fullClient.FirstName }} {{ fullClient && fullClient.LastName }}. Please grab a Danckut selector and a marker form the caddy above and follow the instructions.
            <!-- requires continue -->
          </div>
          <div v-else-if="appointmentStatus === 'is-eo' || appointmentStatus === 'can-check-in'">
            Welcome to your {{ formatTimeOnly(nextAppointment.AppointmentDate) }} appointment, {{ fullClient && fullClient.FirstName }} {{ fullClient && fullClient.LastName }}.
            <!-- requires continue -->
          </div>
        </v-flex>
      </v-layout>

      <v-layout row wrap v-if="isSelectedClient && appointmentStatus && isCheckedIn">
        <v-flex xs8 offset-xs2 class="check-in-client-card__message">
          <div>Feel free to help yourself to a complimentary drink and relax. A technician will be with you shortly, enjoy your experience.</div>
        </v-flex>
      </v-layout>
    </v-card-text>
    <v-card-actions v-if="!isCheckedIn">
      <span v-if="timer">This will close in {{timer}} seconds.</span>
      <v-spacer></v-spacer>

      <v-btn v-if="showSchedulerButton" class="check-in-client-card__button" outline @click.stop="resetCheckIn">No</v-btn>
      <v-btn v-if="showSchedulerButton" class="check-in-client-card__button" color="primary" @click.stop="showScheduler">Yes</v-btn>

      <v-btn v-if="showCancelButton" class="check-in-client-card__button" color="primary" @click.stop="resetCheckIn">Done</v-btn>

      <v-btn v-if="showContinueButton" class="check-in-client-card__button" color="primary" @click.stop="resetCheckIn">Check In</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapGetters } from 'vuex'
import { apis } from '@danckuts/api-js'
import appointmentService from '@/framework/appointment/appointmentService'
import clientService from '@/framework/client/clientService'
import moment from 'moment'

export default {
  props: {
    client: {
      type: Object,
      required: true
    }
  },
  data: function () {
    return {
      appointments: undefined,
      fullClient: undefined,
      hasLoadedAppointments: false,
      hasLoadedClientInfo: false,
      hasLoadedPrevAppointments: false,
      isCheckedIn: false,
      previousAppointments: undefined,
      timer: undefined,
      url: '',
      waitTime: undefined
    }
  },
  watch: {
    'client.refresh': {
      handler (refresh) {
        this.getAppointments()
        this.getCurrentWaitTime()
      }
    }
  },
  computed: {
    ...mapGetters({
      clients: 'checkIn/getClients',
      location: 'user/getLocation',
      selectedClient: 'checkIn/getSelectedClient'
    }),
    countPreviousCompletedAppointments () {
      return this.previousAppointments && this.previousAppointments.length > 0
        ? this.previousAppointments.filter(pv => pv.Status === 40 || pv.Status === 50).length
        : 0
    },
    hasAppointments () {
      return this.appointments && this.appointments.length > 0
    },
    hasLoadedInfo () {
      return this.hasLoadedClientInfo && this.hasLoadedAppointments && this.hasLoadedPrevAppointments
    },
    isScheduleThirty () {
      return this.location.schedule_type === 'schedule.thirty' || this.location.schedule_type === 'schedule.kartThirty'
    },
    isSelectedClient () {
      return this.selectedClient && this.client.Id === this.selectedClient.Id
    },
    nextAppointment () {
      return this.hasAppointments && this.appointments[0]
    },
    showCancelButton () {
      return this.isSelectedClient && (this.appointmentStatus === 'beyond-check-in' || this.appointmentStatus === 'too-late' || this.appointmentStatus === 'too-early' || (!this.isScheduleThirty && this.hasAppointments))
    },
    showContinueButton () {
      return this.isSelectedClient && (this.appointmentStatus === 'is-new-client' || this.appointmentStatus === 'is-eo' || this.appointmentStatus === 'can-check-in') && (this.isScheduleThirty)
    },
    showSchedulerButton () {
      return this.isSelectedClient && (this.appointmentStatus === 'no-appt' || this.appointmentStatus === 'not-today')
    },
    appointmentStatus () {
      if (!this.hasLoadedInfo) {
        return undefined
      }

      if (!this.hasAppointments) {
        this.setAutoTimer()
        return 'no-appt'
      } else {
        if (this.nextAppointment.Status >= 20) {
          this.setAutoTimer()
          return 'beyond-check-in'
        }

        let isToday = moment().diff(this.nextAppointment.AppointmentDate, 'days') === 0
        if (!isToday) {
          this.setAutoTimer()
          return 'not-today'
        }

        let minutesUntil = moment().diff(this.nextAppointment.AppointmentDate, 'minutes')
        if (minutesUntil >= 10) {
          this.setAutoTimer()
          return 'too-late'
        }

        if (minutesUntil <= -20) {
          this.setAutoTimer()
          return 'too-early'
        }

        // auto checkin
        this.checkInClient()

        if (this.countPreviousCompletedAppointments === 0) {
          return 'is-new-client'
        }

        let isEO = this.fullClient.isEO
        if (isEO) {
          return 'is-eo'
        }

        return 'can-check-in'
      }
    }
  },
  mounted () {
    clientService.getClientInformation({ id: this.client.Id }).then(res => {
      this.fullClient = res.data
      this.hasLoadedClientInfo = true
    }).catch(() => {
      this.hasLoadedClientInfo = true
    })

    this.getAppointments()
    this.getCurrentWaitTime()

    clientService.getClientLastPhoto({ id: this.client.Id }).then(res => {
      this.url = res.data
    })
  },
  methods: {
    async checkInClient () {
      await appointmentService.checkInClient({ id: this.nextAppointment.Id, clientId: this.client.Id })
      this.setAutoTimer()
    },
    formatDate (date) {
      return moment(date).format('MMMM Do YYYY, h:mm:ss a')
    },
    formatTimeOnly (date) {
      return moment(date).format('h:mm a')
    },
    formatTimeEarly (date) {
      return moment(date).subtract(20, 'minutes').format('h:mm a')
    },
    getAppointments () {
      clientService.getClientUpcomingAppointments({ id: this.client.Id }).then(res => {
        this.appointments = res.data.data.filter(x => x.Location.Id === this.location.id.toLowerCase())
        this.hasLoadedAppointments = true
      }).catch(() => {
        this.hasLoadedAppointments = true
      })

      clientService.getClientPreviousAppointments({ id: this.client.Id }).then(res => {
        this.previousAppointments = res.data.data
        this.hasLoadedPrevAppointments = true
      }).catch(() => {
        this.hasLoadedPrevAppointments = true
      })
    },
    getCurrentWaitTime () {
      const today = moment().format('YYYY-MM-DD')
      apis.AppointmentService.search(this.location.id.toLowerCase(), today).then(res => {
        const cutting = res.filter(a => a.status === 30)
        const waiting = res.filter(a => a.status <= 20)
        if (cutting.length <= 1 && waiting.length <= 1) {
          this.waitTime = 'zero'
          return
        }
        const isEven = ((waiting.length + 1) % 2) === 0
        const calcWaiting = waiting.filter((w, i) => {
          if (isEven) {
            return (i % 2 === 1)
          }
          return (i % 2 === 0)
        })
        this.waitTime = calcWaiting.length * 25
      })
    },
    resetCheckIn () {
      this.stopTimer()
      this.$store.commit('checkIn/resetCheckIn')
    },
    runTimer () {
      setTimeout(() => {
        if (this.timer < 1) {
          this.resetCheckIn()
        } else {
          this.timer--
          this.runTimer()
        }
      }, 1000)
    },
    selectClient (client) {
      this.$store.commit('checkIn/updateSelectedClient', client)
    },
    setAutoTimer (time) {
      this.timer = time || 15
      this.runTimer()
    },
    showScheduler () {
      this.stopTimer()
      if (this.isScheduleThirty) {
        this.$emit('openScheduler')
      } else {
        const today = moment().format('YYYY-MM-DD')
        const time = moment().add('min', 1).format('HH:mm')
        this.$emit('selectDate', { date: today, time: time }, today)
      }
    },
    stopTimer () {
      if (this.timer) {
        this.timer = undefined
      }
    }
  }
}
</script>

<style lang="scss">
.check-in-client-card {
  &__headline {
    display: block;

    span {
      display: inline-block;
      font-size: 1.4em;
      font-weight: bold;
      padding-right: 5px;
    }
  }

  &__selected {
    background-color: red;
  }

  &__button {
    text-transform: capitalize;
  }

  &__message {
    font-size: 1.2em;
    font-weight: 500;
  }

  &__value {
    font-size: 1.2em;
    font-weight: 600;

    small {
      font-size: 0.9em;
      font-weight: normal;
    }
  }
}
</style>
